<template>
  <LiveRegion
    :message="message"
    :politeness="priority"
    :role="priority === 'assertive' ? 'alert' : 'status'"
  />
</template>

<script setup lang="ts">
/**
 * The application's single live-region renderer, mounted once in `app.vue` so
 * it is present on every route before any message is set.
 *
 * Callers do not use this component directly. They use Nuxt's built-in
 * announcer, which this subscribes to:
 *
 *   const { polite, assertive } = useAnnouncer()
 *   polite(t('dashboard.messages.changesSaved'))
 *   assertive(t('dashboard.messages.pleaseReviewTheHighlightedFields'))
 *
 * `useAnnouncer()` returns the same instance app-wide (Nuxt caches it on the
 * Nuxt app), so a caller anywhere reaches this renderer without prop drilling
 * or its own region. Per-field messages stay with their field via FormField;
 * this is for announcements that have no on-screen owner.
 */
import { computed } from 'vue'
import { useAnnouncer } from '#imports'

import LiveRegion from '~/design-system/atoms/LiveRegion/LiveRegion.atom.vue'

const { message, politeness } = useAnnouncer()

// Nuxt's announcer also models 'off', which has no live-region meaning here:
// the region stays mounted and simply says nothing until a message arrives.
const priority = computed(() => (politeness.value === 'assertive' ? 'assertive' : 'polite'))
</script>
